import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { AgentService, ZoneDto } from '../../../services/agent.service';
import {
  AgentLeaderboardResponse,
  AgentLeaderboardItem,
  AgentLeaderboardFilter
} from '../../../services/agent-leaderboard.models';
import { ExternalDataService } from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';
import { AgentScoreBreakdownComponent } from './agent-score-breakdown/agent-score-breakdown.component';
import { AgentAvatarComponent } from '../agent-avatar/agent-avatar.component';

type DatePreset = 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear' | 'custom';
type TabCategory = 'all' | 'vouchers' | 'value' | 'pickups' | 'rating';

@Component({
  selector: 'app-agent-leaderboard',
  standalone: true,
  imports: [CommonModule, FormsModule, AgentScoreBreakdownComponent, AgentAvatarComponent],
  templateUrl: './agent-leaderboard.component.html',
  styleUrls: ['./agent-leaderboard.component.css']
})
export class AgentLeaderboardComponent implements OnInit, OnDestroy {
  loading: boolean = false;
  errorMessage: string = '';
  data: AgentLeaderboardResponse | null = null;
  filteredAgents: AgentLeaderboardItem[] = [];

  // Filter state
  selectedPreset: DatePreset = 'last30days';
  fromDate: string = '';
  toDate: string = '';
  selectedZone: string = '';
  searchQuery: string = '';
  activeTab: TabCategory = 'all';

  // Sort state
  sortColumn: keyof AgentLeaderboardItem | 'rank' = 'rank';
  sortAscending: boolean = true;

  // Selected agent for drilldown modal
  selectedAgent: AgentLeaderboardItem | null = null;
  isModalOpen: boolean = false;

  // Dropdowns
  zones: ZoneDto[] = [];

  // Presets definition
  presets: { id: DatePreset; label: string; icon: string }[] = [
    { id: 'last7days', label: 'Last 7 Days', icon: 'fa-calendar-day' },
    { id: 'last30days', label: 'Last 30 Days', icon: 'fa-calendar-alt' },
    { id: 'thisMonth', label: 'This Month', icon: 'fa-calendar' },
    { id: 'lastMonth', label: 'Last Month', icon: 'fa-history' },
    { id: 'thisQuarter', label: 'This Quarter', icon: 'fa-chart-pie' },
    { id: 'thisYear', label: 'This Year', icon: 'fa-calendar-check' },
    { id: 'custom', label: 'Custom Range', icon: 'fa-sliders-h' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private agentService: AgentService,
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.applyPreset(this.selectedPreset);
    this.loadZones();
    this.loadLeaderboard();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadZones(): void {
    this.agentService.getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones: ZoneDto[]) => {
          this.zones = zones || [];
        },
        error: (err: unknown) => {
          console.error('Failed to load zones', err);
        }
      });
  }

  selectPreset(preset: DatePreset): void {
    this.selectedPreset = preset;
    this.applyPreset(preset);
    if (preset !== 'custom') {
      this.loadLeaderboard();
    }
  }

  applyPreset(preset: DatePreset): void {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (preset) {
      case 'last7days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        this.fromDate = this.formatDate(start);
        this.toDate = this.formatDate(today);
        break;
      }
      case 'last30days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        this.fromDate = this.formatDate(start);
        this.toDate = this.formatDate(today);
        break;
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        this.fromDate = this.formatDate(start);
        this.toDate = this.formatDate(today);
        break;
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        this.fromDate = this.formatDate(start);
        this.toDate = this.formatDate(end);
        break;
      }
      case 'thisQuarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), quarter * 3, 1);
        this.fromDate = this.formatDate(start);
        this.toDate = this.formatDate(today);
        break;
      }
      case 'thisYear': {
        const start = new Date(now.getFullYear(), 0, 1);
        this.fromDate = this.formatDate(start);
        this.toDate = this.formatDate(today);
        break;
      }
      case 'custom':
        // Keep current dates
        break;
    }
  }

  onCustomDateChange(): void {
    if (this.fromDate && this.toDate) {
      this.loadLeaderboard();
    }
  }

  onZoneChange(): void {
    this.loadLeaderboard();
  }

  loadLeaderboard(): void {
    this.loading = true;
    this.errorMessage = '';

    const filter: AgentLeaderboardFilter = {
      fromDate: this.fromDate ? new Date(this.fromDate + 'T00:00:00Z').toISOString() : undefined,
      toDate: this.toDate ? new Date(this.toDate + 'T23:59:59Z').toISOString() : undefined,
      geoZoneIds: this.selectedZone ? this.selectedZone : undefined
    };

    this.agentService.getAgentLeaderboard(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AgentLeaderboardResponse) => {
          this.data = response;
          this.pictureByAgent = new Map(
            (response.agents || []).filter(a => a.profilePictureUrl).map(a => [a.agentId, a.profilePictureUrl as string])
          );
          this.applyFiltersAndSort();
          this.loading = false;
        },
        error: (err: unknown) => {
          console.error('Failed to load agent leaderboard', err);
          this.errorMessage = 'Failed to load agent leaderboard. Please try again.';
          this.loading = false;
        }
      });
  }

  setTab(tab: TabCategory): void {
    this.activeTab = tab;
    switch (tab) {
      case 'vouchers':
        this.sortColumn = 'voucherConversionRate';
        this.sortAscending = false;
        break;
      case 'value':
        this.sortColumn = 'totalGrossValue';
        this.sortAscending = false;
        break;
      case 'pickups':
        this.sortColumn = 'totalCollections';
        this.sortAscending = false;
        break;
      case 'rating':
        this.sortColumn = 'averageRating';
        this.sortAscending = false;
        break;
      case 'all':
      default:
        this.sortColumn = 'rank';
        this.sortAscending = true;
        break;
    }
    this.applyFiltersAndSort();
  }

  sortBy(column: keyof AgentLeaderboardItem | 'rank'): void {
    if (this.sortColumn === column) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.sortColumn = column;
      this.sortAscending = (column === 'rank');
    }
    this.applyFiltersAndSort();
  }

  onSearchChange(): void {
    this.applyFiltersAndSort();
  }

  applyFiltersAndSort(): void {
    if (!this.data || !this.data.agents) {
      this.filteredAgents = [];
      return;
    }

    let list = [...this.data.agents];

    // Search query filter
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(a =>
        a.agentName.toLowerCase().includes(q) ||
        a.phoneNumber.includes(q) ||
        a.agentId.toLowerCase().includes(q) ||
        (a.vehicleNumber && a.vehicleNumber.toLowerCase().includes(q))
      );
    }

    // Sort list
    list.sort((a, b) => {
      let valA: any = a[this.sortColumn as keyof AgentLeaderboardItem];
      let valB: any = b[this.sortColumn as keyof AgentLeaderboardItem];

      if (valA === undefined || valA === null) valA = 0;
      if (valB === undefined || valB === null) valB = 0;

      if (typeof valA === 'string') {
        return this.sortAscending
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return this.sortAscending ? valA - valB : valB - valA;
    });

    this.filteredAgents = list;
  }

  private pictureByAgent = new Map<string, string>();

  /** Profile picture link for a category leader. */
  pictureOf(agentId: string | null | undefined): string | null {
    return (agentId && this.pictureByAgent.get(agentId)) || null;
  }

  getTop3Agents(): AgentLeaderboardItem[] {
    if (!this.data || !this.data.agents) return [];
    return this.data.agents.slice(0, 3);
  }

  openDrilldown(agent: AgentLeaderboardItem): void {
    this.selectedAgent = agent;
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selectedAgent = null;
  }

  exportCsv(): void {
    if (!this.filteredAgents.length) return;

    const headers = [
      'Rank',
      'Agent Name',
      'Phone Number',
      'Vehicle Number',
      'Completed Pickups',
      'Voucher Pickups',
      'Voucher Conversion Rate (%)',
      'Voucher Value (₹)',
      'Total Value Paid (₹)',
      'Gross Profit (₹)',
      'Agent Voucher Commission (₹)',
      'Avg Rating',
      'Ratings Count',
      'Completion Rate (%)',
      'Check-in Days',
      'Leave Days',
      'Unplanned Leave Days',
      'Score'
    ];

    const rows = this.filteredAgents.map(a => [
      a.rank,
      `"${a.agentName.replace(/"/g, '""')}"`,
      `"${a.phoneNumber}"`,
      `"${a.vehicleNumber || ''}"`,
      a.totalCollections,
      a.voucherCollectionsCount,
      a.voucherConversionRate,
      a.totalVoucherValue,
      a.totalGrossValue,
      a.totalProfit,
      a.totalAgentVoucherCommission,
      a.averageRating,
      a.totalRatingsCount,
      a.completionRate,
      a.checkInDaysCount,
      a.leaveDays,
      a.unplannedLeaveDays,
      a.compositeScore
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Agent_Leaderboard_${this.fromDate}_to_${this.toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getZoneName(zoneId: string): string {
    const zone = this.zones.find(z => z.id === zoneId);
    return zone ? zone.name : zoneId;
  }

  getStarArray(rating: number): number[] {
    const fullStars = Math.floor(rating);
    return Array.from({ length: 5 }, (_, i) => i < fullStars ? 1 : (i < rating ? 0.5 : 0));
  }

  private formatDate(date: Date): string {
    // Build from local date parts, not toISOString() — that converts to UTC first, which
    // shifts the date back a day for any timezone ahead of UTC (e.g. IST, UTC+5:30).
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
